import { motion } from 'motion/react';

import { useTranslation } from '../i18n/hooks';

export default function ServicesSection() {
  const t = useTranslation();

  return (
    <motion.section
      id="services"
      className="overflow-x-hidden px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <motion.div
        className="mb-12 text-center sm:mb-16"
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <motion.h2
          className="mb-3 px-2 text-2xl font-bold text-gray-900 sm:mb-4 sm:text-3xl md:text-3xl lg:text-4xl"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          {t.services.title}
        </motion.h2>
        <motion.p
          className="px-2 text-base text-gray-900 sm:text-lg md:text-xl"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          viewport={{ once: true }}
        >
          {t.services.disclaimer}
        </motion.p>
      </motion.div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
        {/* Video Consultation Card */}
        <motion.div
          className="rounded-2xl bg-[#2ab25e] p-4 text-white sm:rounded-3xl sm:p-6 md:p-8"
          initial={{ x: -50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
          whileHover={{
            scale: 1.02,
            boxShadow: '0 20px 40px rgba(42, 178, 94, 0.3)',
          }}
        >
          <motion.div
            className="mb-4 sm:mb-6"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="mb-3 text-lg font-bold sm:mb-4 sm:text-xl md:text-2xl">
              {t.services.videoConsultation.title}
            </h3>
            <p className="text-base font-bold sm:text-lg md:text-xl">
              {t.services.videoConsultation.description}
            </p>
          </motion.div>
          <motion.div
            className="rounded-2xl bg-white/10 p-4 sm:rounded-3xl sm:p-6"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="space-y-3 sm:space-y-4">
              {t.services.videoConsultation.features.map((text, index) => (
                <motion.div
                  key={index}
                  className="flex items-start justify-start gap-3 sm:gap-4"
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="mt-0.5 h-5 w-5 shrink-0 sm:h-6 sm:w-6"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      delay: 1 + index * 0.1,
                      duration: 0.3,
                      type: 'spring',
                    }}
                    viewport={{ once: true }}
                  >
                    <img
                      src="/images/check.svg"
                      alt="Check Icon"
                      // width={20}
                      // height={20}
                    />
                  </motion.div>
                  <p className="text-sm leading-relaxed font-bold sm:text-base md:text-lg">
                    {text}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Second Opinion Card */}
        <motion.div
          className="flex flex-col rounded-2xl bg-blue-900 p-4 text-white sm:rounded-3xl sm:p-6 md:p-8"
          initial={{ x: 50, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
          whileHover={{
            scale: 1.02,
            boxShadow: '0 20px 40px rgba(30, 58, 138, 0.3)',
          }}
        >
          <motion.div
            className="mb-4 sm:mb-6"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="mb-3 text-lg font-bold sm:mb-4 sm:text-xl md:text-2xl">
              {t.services.secondOpinion.title}
            </h3>
            <p className="text-base font-bold sm:text-lg md:text-xl">
              {t.services.secondOpinion.description}
            </p>
          </motion.div>
          <motion.div
            className="mt-auto rounded-2xl bg-white/10 p-4 sm:rounded-3xl sm:p-6"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="space-y-3 sm:space-y-4">
              {t.services.secondOpinion.features.map((text, index) => (
                <motion.div
                  key={index}
                  className="flex items-start justify-start gap-3 sm:gap-4"
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 1 + index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="mt-0.5 h-5 w-5 shrink-0 sm:h-6 sm:w-6"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      delay: 1.2 + index * 0.1,
                      duration: 0.3,
                      type: 'spring',
                    }}
                    viewport={{ once: true }}
                  >
                    <img
                      src="/images/check.svg"
                      alt="Check Icon"
                      // width={20}
                      // height={20}
                    />
                  </motion.div>
                  <p className="text-sm leading-relaxed font-bold sm:text-base md:text-lg">
                    {text}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
}
